import React from 'react';
import { Controller } from 'react-hook-form';
import PropTypes from 'prop-types';

const FormInput = ({
	name,
	control,
	label,
	type = 'text',
	placeholder,
	required = false,
	disabled = false,
	maxLength,
	minLength,
	pattern,
	validate,
	rules = {},
	className = '',
	showCharCount = false,
	helpText,
	rows,
	...props
}) => {
	// Build validation rules
	const validationRules = {
		required: required
			? typeof required === 'string'
				? required
				: `${label} is required`
			: false,
		...(maxLength && {
			maxLength: {
				value: maxLength,
				message: `${label} cannot exceed ${maxLength} characters`,
			},
		}),
		...(minLength && {
			minLength: {
				value: minLength,
				message: `${label} must be at least ${minLength} characters`,
			},
		}),
		...(pattern && {
			pattern: { value: pattern, message: `${label} format is invalid` },
		}),
		...(validate && { validate }),
		...rules,
	};

	return (
		<Controller
			name={name}
			control={control}
			rules={validationRules}
			render={({ field, fieldState: { error } }) => (
				<div className={`mb-3 ${className}`}>
					{label && (
						<label htmlFor={name} className="form-label">
							{label} {required && <span className="text-danger">*</span>}
						</label>
					)}

					{type === 'textarea' ? (
						<textarea
							{...field}
							{...props}
							id={name}
							className={`form-control ${error ? 'is-invalid' : ''}`}
							placeholder={placeholder}
							disabled={disabled}
							maxLength={maxLength}
							rows={rows || 3}
						/>
					) : (
						<input
							{...field}
							{...props}
							type={type}
							id={name}
							className={`form-control ${error ? 'is-invalid' : ''}`}
							placeholder={placeholder}
							disabled={disabled}
							maxLength={maxLength}
						/>
					)}

					{error && <div className="invalid-feedback">{error.message}</div>}

					{showCharCount && maxLength && (
						<div className="form-text">
							{field.value?.length || 0}/{maxLength} characters
						</div>
					)}

					{helpText && !error && <div className="form-text">{helpText}</div>}
				</div>
			)}
		/>
	);
};

FormInput.propTypes = {
	name: PropTypes.string.isRequired,
	control: PropTypes.object.isRequired,
	label: PropTypes.string,
	type: PropTypes.oneOf([
		'text',
		'email',
		'password',
		'number',
		'tel',
		'url',
		'textarea',
	]),
	placeholder: PropTypes.string,
	required: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
	disabled: PropTypes.bool,
	maxLength: PropTypes.number,
	minLength: PropTypes.number,
	pattern: PropTypes.instanceOf(RegExp),
	validate: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
	rules: PropTypes.object,
	className: PropTypes.string,
	showCharCount: PropTypes.bool,
	helpText: PropTypes.string,
	rows: PropTypes.number,
};

export default FormInput;
